import React, { useEffect, useRef, useState } from "react";
import { Icon } from "../icons";
import { crewLines } from "../lib/crew";
import { addDays, fmtDate, fmtDateTime, todayStr } from "../lib/format";
import { aiCallFor, aiCallTag } from "../lib/aiCalls";
import { money } from "../lib/hourly";

const AUTOSAVE_MS = 3000; // save 3s after the coordinator stops typing
const SAVED_VISIBLE_MS = 2200;

/**
 * Note textarea with per-field save feedback: auto-saves 3s after typing stops
 * (and immediately on blur), and shows an inline "Saving…" / "✓ Saved" badge on
 * the field itself — so it's clear WHICH note was saved, without a global toast.
 */
export const NoteTextarea = ({ job, fieldKey, hint, onChange, rows = 2 }) => {
  const [status, setStatus] = useState(""); // "" | "pending" | "saved"
  const debounceRef = useRef(null);
  const hideRef = useRef(null);
  const current = () => (job.s2Notes && job.s2Notes[fieldKey]) || "";

  useEffect(
    () => () => {
      clearTimeout(debounceRef.current);
      clearTimeout(hideRef.current);
    },
    [],
  );

  const commit = (value) => {
    clearTimeout(debounceRef.current);
    if (value === current()) {
      setStatus("");
      return;
    }
    onChange(job.id, fieldKey, value);
    setStatus("saved");
    clearTimeout(hideRef.current);
    hideRef.current = setTimeout(() => setStatus(""), SAVED_VISIBLE_MS);
  };

  const onInput = (e) => {
    const value = e.target.value;
    clearTimeout(debounceRef.current);
    if (value === current()) {
      setStatus("");
      return;
    }
    setStatus("pending");
    debounceRef.current = setTimeout(() => commit(value), AUTOSAVE_MS);
  };

  return (
    <div className="s2-note-wrap">
      <textarea
        rows={rows}
        placeholder={hint || "Add a note..."}
        defaultValue={current()}
        onInput={onInput}
        onBlur={(e) => commit(e.target.value)}
      />
      {status ? (
        <span className={`s2-note-badge s2-note-badge-${status}`}>
          {status === "saved" ? (
            <>
              <Icon name="check" /> Saved
            </>
          ) : (
            "Saving…"
          )}
        </span>
      ) : null}
    </div>
  );
};

/**
 * Button with built-in "✓ Saved" confirmation. `onSave` runs the save and may
 * return false to veto it (e.g. empty input) — nothing flashes then. On any
 * other return the label flips to a green "✓ Saved" for a couple seconds so the
 * coordinator can SEE the click took effect. Pair with `disabled` to grey it
 * out when there's nothing to save.
 */
export const SaveButton = ({
  onSave,
  label,
  savedLabel = "Saved",
  idleIcon = "check",
  disabled = false,
  primary = false,
  small = true,
}) => {
  const [saved, setSaved] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const click = () => {
    if (disabled) return;
    if (onSave() === false) return; // veto: invalid/empty
    setSaved(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setSaved(false), SAVED_VISIBLE_MS);
  };
  const cls = ["btn", small ? "small" : "", saved ? "success" : primary ? "primary" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" className={cls} disabled={disabled} onClick={click}>
      <Icon name={saved ? "check" : idleIcon} /> {saved ? savedLabel : label}
    </button>
  );
};

/**
 * Setter for s2 note fields. Injects the job id, tolerates BOTH calling styles
 * — 2-arg `set(key, value)` from inline textareas and 3-arg
 * `set(id, key, value)` from S2Field — and skips no-op writes so blurring an
 * untouched field never triggers a pointless save (or a "Saved" flash).
 */
export const makeS2Setter = (job, setS2Note) => (a, b, c) => {
  const threeArg = c !== undefined;
  const id = threeArg ? a : job.id;
  const key = threeArg ? b : a;
  const value = threeArg ? c : b;
  const current = (job.s2Notes && job.s2Notes[key]) || "";
  if (value !== current) setS2Note(id, key, value);
};

/** Free-text note bound to job.s2Notes[key] — mirrors s2Field() in the prototype. */
export const S2Field = ({ job, fieldKey, label, hint, onChange, children }) => (
  <div className="field">
    <label>{children || label}</label>
    <NoteTextarea job={job} fieldKey={fieldKey} hint={hint} onChange={onChange} />
  </div>
);

/** Yes/No toggle plus a note — mirrors s2FieldYN(). */
export const S2FieldYN = ({ job, fieldKey, label, hint, onChange, onToggleYN }) => {
  const yn = (job.s2YN && job.s2YN[fieldKey]) || "";
  return (
    <div className="field">
      <label>{label}</label>
      <div className="toolbar-row" style={{ margin: "0 0 6px 0" }}>
        <button
          type="button"
          className={`btn small ${yn === "yes" ? "success" : ""}`}
          onClick={() => onToggleYN(fieldKey, "yes")}
        >
          Yes
        </button>
        <button
          type="button"
          className={`btn small ${yn === "no" ? "danger" : ""}`}
          onClick={() => onToggleYN(fieldKey, "no")}
        >
          No
        </button>
      </div>
      <NoteTextarea job={job} fieldKey={fieldKey} hint={hint} onChange={onChange} />
    </div>
  );
};

/** Yes/No toggle with the Zoho included/not-included label — mirrors ynFieldNoNotes(). */
export const YnFieldNoNotes = ({ job, fieldKey, label, onToggleYN }) => {
  const yn = (job.s2YN && job.s2YN[fieldKey]) || "";
  return (
    <div className="field">
      <label>
        <ZohoLabel text={label} included={yn === "yes"} />
      </label>
      <div className="toolbar-row" style={{ margin: 0 }}>
        <button
          type="button"
          className={`btn small ${yn === "yes" ? "success" : ""}`}
          onClick={() => onToggleYN(fieldKey, "yes")}
        >
          Yes
        </button>
        <button
          type="button"
          className={`btn small ${yn === "no" ? "danger" : ""}`}
          onClick={() => onToggleYN(fieldKey, "no")}
        >
          No
        </button>
      </div>
    </div>
  );
};

const smallBadge = { marginLeft: 6, fontSize: 9, padding: "2px 6px", verticalAlign: "middle" };
const plainBadge = { fontSize: 9, padding: "2px 6px" };

export const ZohoLabel = ({ text, included }) => (
  <>
    {text}{" "}
    <span className={`badge ${included ? "resolved" : "plain"}`} style={smallBadge}>
      {included ? "✓ Included" : "✗ Not included"}
    </span>
  </>
);

export const ZohoBadgeVal = ({ included }) => (
  <span className={`badge ${included ? "resolved" : "plain"}`} style={plainBadge}>
    {included ? "✓ Included" : "✗ Not included"}
  </span>
);

export const YesNoBadge = ({ value }) => (
  <span className={`badge ${value ? "resolved" : "plain"}`} style={plainBadge}>
    {value ? "Yes" : "No"}
  </span>
);

/**
 * COI state of one address. `required` false = a private house (Zoho building
 * type), which needs no certificate; a missing flag counts as required.
 */
export const CoiBadge = ({ completed, required = true }) => {
  if (required === false) {
    return (
      <span className="badge plain" style={plainBadge}>
        Not required
      </span>
    );
  }
  return completed ? (
    <span className="badge resolved" style={plainBadge}>
      ✓ Yes
    </span>
  ) : (
    <span className="badge open" style={plainBadge}>
      COI Required
    </span>
  );
};

/**
 * The crew the planner dispatched (job.dispatchedCrew), one line per day,
 * with the MC's own note underneath; the note alone, or a dash, when the
 * planner has nobody on the job yet.
 */
export const PlannerCrew = ({ job }) => {
  const lines = crewLines(job);
  const note = (job.s2Notes && job.s2Notes.dispatchedCrew) || "";
  if (!lines.length) {
    return (
      <div className="field">
        <div className="val">{note || "—"}</div>
      </div>
    );
  }
  return (
    <div className="field">
      {lines.map((line) => (
        <div className="val" key={line}>
          {line}
        </div>
      ))}
      {note ? <div className="liab-sub">MC note: {note}</div> : null}
    </div>
  );
};

export const SectionLabel = ({ icon, children, style }) => (
  <div className="section-label" style={style}>
    <Icon name={icon} />
    {children}
  </div>
);

export const ReadField = ({ label, children, style }) => (
  <div className="field" style={style}>
    <label>{label}</label>
    <div className="val">{children}</div>
  </div>
);

/** Who booked the job — the Zoho lead owner, pulled live; the MC can't edit it. */
export const SalespersonField = ({ job }) => (
  <ReadField label="Salesperson (Zoho)">{job.salesperson || "—"}</ReadField>
);

export const DestCard = ({ icon, label, address, stats }) => (
  <div className="dest-card">
    <div className="dest-card-head">
      <div className="dest-card-icon">
        <Icon name={icon} />
      </div>
      <div>
        <div className="dest-card-label">{label}</div>
        <div className="dest-card-address">{address}</div>
      </div>
    </div>
    <div className="dest-card-grid">
      {stats.map(([l, v]) => (
        <div className="dest-card-stat" key={l}>
          <span className="dcs-label">{l}</span>
          <span className="dcs-val">{v}</span>
        </div>
      ))}
    </div>
  </div>
);

/** Fired by the footer's "Schedule callback" button to open or close the scheduler. */
export const TOGGLE_CALLBACK_EVENT = "mcops:toggle-callback";

/**
 * Callback scheduler at the top of the Introduction Call tab. It stays hidden
 * until the footer's "Schedule callback" button asks for it, so the same
 * button works in the popup and on the job page.
 */
export const CallbackWidget = ({ job, onSave }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const today = todayStr();
  const [date, setDate] = useState(job.callbackDate || today);
  const [time, setTime] = useState(job.callbackTime || "09:00");

  useEffect(() => {
    const toggle = () => setOpen((v) => !v);
    window.addEventListener(TOGGLE_CALLBACK_EVENT, toggle);
    return () => window.removeEventListener(TOGGLE_CALLBACK_EVENT, toggle);
  }, []);

  useEffect(() => {
    if (open && rootRef.current && rootRef.current.scrollIntoView)
      rootRef.current.scrollIntoView({ block: "nearest" });
  }, [open]);

  return (
    <div ref={rootRef}>
      {open ? (
        <div
          style={{
            margin: "0 0 14px 0",
            padding: "10px 12px",
            background: "var(--ink-50)",
            border: "1px solid var(--border)",
            borderRadius: 10,
          }}
        >
          <div className="field-row">
            <div className="field">
              <label>Callback date (within 3 days)</label>
              <select value={date} onChange={(e) => setDate(e.target.value)}>
                {[0, 1, 2, 3].map((n) => {
                  const d = addDays(today, n);
                  return (
                    <option value={d} key={d}>
                      {n === 0 ? "Today" : n === 1 ? "Tomorrow" : fmtDate(d)}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="field">
              <label>Callback time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>
          <div className="toolbar-row">
            <button
              type="button"
              className="btn primary small"
              onClick={() => {
                onSave(date, time);
                setOpen(false);
              }}
            >
              <Icon name="check" />
              Save callback
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const PAYMENT_METHODS = [
  ["credit_card", "Credit card"],
  ["wire", "Wire transfer"],
  ["check", "Check"],
  ["debit_card", "Debit card"],
];

export const TRANSIT_PAYMENT_METHODS = [
  ["", "Not yet captured"],
  ["card_on_file", "Card on file"],
  ["new_card", "New card"],
  ["check", "Check"],
  ["wire", "Wire transfer"],
];

/** Yes / No segmented toggle bound to job.s2YN[fieldKey] — the prototype's `.seg`. */
export const SegYN = ({ job, fieldKey, label, onToggleYN, fallback = "" }) => {
  const yn = (job.s2YN && job.s2YN[fieldKey]) || fallback;
  return (
    <div className="seg" role="group" aria-label={label}>
      <button
        type="button"
        className={yn === "yes" ? "on-yes" : ""}
        aria-pressed={yn === "yes"}
        onClick={() => onToggleYN(fieldKey, "yes")}
      >
        Yes
      </button>
      <button
        type="button"
        className={yn === "no" ? "on-no" : ""}
        aria-pressed={yn === "no"}
        onClick={() => onToggleYN(fieldKey, "no")}
      >
        No
      </button>
    </div>
  );
};

/**
 * Liability, as in the approved prototype: coverage and crates come from Zoho
 * (read-only), the MC notes fragile items and whether coverage was explained.
 * Shared by the Introduction and Confirmation calls.
 */
export const LiabilityCard = ({ job, set, actions }) => {
  // The amount is only on the proposal document, so the card says whether coverage is on the file
  // (an insurance service line or Zoho's additional insurance charge) and names the line.
  const included = Boolean(job.coverageIncluded) || Number(job.replacementCoverageAmount) > 0;
  const line = included ? String(job.coverageLine || "").trim() : "";
  const crates = Array.isArray(job.cratesList) ? job.cratesList : [];
  return (
    <div className="liab-card">
      <div className="liab-row">
        <div>
          <div>Replacement coverage included</div>
          <div className="liab-sub">{line ? `From Zoho · ${line}` : "From Zoho"}</div>
        </div>
        <div className="liab-val">{included ? "Included" : "Not included"}</div>
      </div>
      <div className="liab-block">
        <div>Crates included</div>
        <div className="liab-sub">Synced with Zoho</div>
        {crates.length ? (
          crates.map((c, i) => (
            <div className="liab-crate" key={`${c.name}-${i}`}>
              <span>
                {c.name}
                {c.appliesTo ? <span className="liab-crate-item"> · {c.appliesTo}</span> : null}
              </span>
              <span className="liab-val" style={{ fontSize: 13 }}>
                {money(c.price)}
              </span>
            </div>
          ))
        ) : (
          <div className="liab-sub" style={{ marginTop: 6 }}>
            No crates on this job
          </div>
        )}
      </div>
      <div className="liab-block">
        <div>Fragile or high-value items</div>
        <NoteTextarea
          job={job}
          fieldKey="fragileItems"
          hint="Add a note (art, antiques, pianos, heirlooms)..."
          onChange={set}
        />
      </div>
      <div className="liab-block">
        <div className="liab-top">
          <div>Coverage explained to client</div>
          <SegYN
            job={job}
            fieldKey="coverageExplained"
            label="Coverage explained to client"
            onToggleYN={(k, v) => actions.toggleYN(job.id, k, v)}
          />
        </div>
        <NoteTextarea job={job} fieldKey="coverageExplainedNote" hint="Add a note..." onChange={set} />
      </div>
    </div>
  );
};

/** Payment method as buttons, plus the card on file when Zoho has one. */
export const PaymentMethodField = ({ job, actions }) => (
  <div className="field pay-field">
    <label>Payment method</label>
    <div className="toolbar-row" style={{ margin: 0 }}>
      {PAYMENT_METHODS.map(([value, label]) => (
        <button
          type="button"
          key={value}
          className={`btn small ${job.paymentMethod === value ? "primary" : ""}`}
          aria-pressed={job.paymentMethod === value}
          onClick={() => actions.setJobField(job.id, "paymentMethod", value)}
        >
          {label}
        </button>
      ))}
    </div>
    {job.cardLast4 ? (
      <div className="liab-sub" style={{ marginTop: 8 }}>
        Card on file: •••• {job.cardLast4}
        {job.cardType ? ` (${job.cardType})` : ""}
      </div>
    ) : null}
  </div>
);

/** Introduction call payments, as in the prototype's paymentsCardHTML. */
export const PaymentsCard = ({ job, set, actions }) => (
  <>
    <PaymentMethodField job={job} actions={actions} />
    <div className="field pay-field">
      <label>Payment schedule explained</label>
      <SegYN
        job={job}
        fieldKey="paymentSchedule"
        label="Payment schedule explained"
        onToggleYN={(k, v) => actions.toggleYN(job.id, k, v)}
      />
    </div>
    <div className="field pay-field">
      <label>Payment notes</label>
      <NoteTextarea job={job} fieldKey="paymentNotes" hint="Add a note..." onChange={set} />
    </div>
  </>
);

/** The services the job includes, as chips — the prototype's includedServicesHTML. */
export const IncludedServices = ({ job }) => {
  const items = [];
  if (job.carpenterIncluded) items.push("Carpenter service");
  if (job.assemblyIncluded) items.push("Assembly");
  if (job.disassemblyIncluded) items.push("Disassembly");
  if (job.tvServiceIncluded) items.push("TV service");
  if (job.disposalIncluded) items.push("Disposal service");
  (job.additionalServices || []).forEach((x) => items.push(x));
  return (
    <div className="field">
      <label>Included services</label>
      {items.length ? (
        <div className="val" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {items.map((x, i) => (
            <span className="badge resolved" key={`${x}-${i}`}>
              <Icon name="check" />
              {x}
            </span>
          ))}
        </div>
      ) : (
        <div className="val" style={{ color: "var(--ink-400)", fontWeight: 500 }}>
          No special services included
        </div>
      )}
    </div>
  );
};

/** Extra services sold on top of the quote, from Zoho. */
export const AdditionalServicesTable = ({ items }) => {
  const rows = Array.isArray(items) ? items : [];
  return (
    <div className="field">
      <label>Additional Services</label>
      {rows.length ? (
        <div className="mc-table-wrap">
          <table className="mc-table">
            <thead>
              <tr>
                <th>Quantity</th>
                <th>Service</th>
                <th>Applies to item</th>
                <th>Scheduled date</th>
                <th>Price ($)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.quantity ?? "—"}</td>
                  <td>{r.service || "—"}</td>
                  <td>{r.appliesTo || "—"}</td>
                  <td>{r.scheduledDate ? fmtDate(r.scheduledDate) : "—"}</td>
                  <td>{r.price != null && r.price !== "" ? money(r.price) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="val" style={{ color: "var(--ink-400)", fontWeight: 500 }}>
          No additional services on this job
        </div>
      )}
    </div>
  );
};

/**
 * The AI voice agent's call of this kind (Asana 1219524311157587): how it went
 * and its summary, so the MC knows the automated flow ran. Nothing if none yet.
 */
export const AiCallBlock = ({ job, type }) => {
  const call = aiCallFor(job, type);
  const tag = aiCallTag(call);
  if (!tag) return null;
  return (
    <>
      <SectionLabel icon="phone">{type === "post_move" ? "Automated post-move check-in" : "Automated confirmation call"}</SectionLabel>
      <div className="dest-card" style={{ padding: "10px 12px" }}>
        <span className={`badge ${tag.cls}`}>
          <span className="bdot" />
          {tag.label}
        </span>
        <div className="meta" style={{ marginTop: 6 }}>{fmtDateTime(call.endedAt)}</div>
        {call.summary ? <div style={{ marginTop: 6, fontSize: 12.5 }}>{call.summary}</div> : null}
      </div>
    </>
  );
};
